import { appStorage } from './betaStorage'
export type SessionNoteStatus = 'draft' | 'private' | 'shared'

export type SessionNoteRecipient = 'participant' | 'parent_nominee' | 'support_workers' | 'support_coordinator' | 'allied_health'

export interface BehaviourSessionStrategy {
  id: string
  participantId?: string
  title: string
  when: string
  doThis: string
  avoid: string
  addToSupportStrategies: boolean
}

export interface BehaviourSessionGoalLink {
  id: string
  participantId?: string
  goalTitle: string
  progressNote: string
  addToGoalProgress: boolean
}

export interface BehaviourSessionNote {
  id: string
  participantId: string
  participantName: string
  clinicianId: string
  clinicianName: string
  clinicianRole: string
  sessionDate: string
  sessionType: string
  topic: string
  clinicalNote: string
  careTeamSummary: string
  strategies: BehaviourSessionStrategy[]
  goalLinks: BehaviourSessionGoalLink[]
  recipients: SessionNoteRecipient[]
  status: SessionNoteStatus
  createdAt: string
  updatedAt: string
}

const NOTES_KEY = 'schub.demo.behaviourSessionNotes.v1'
const STRATEGIES_KEY = 'schub.demo.behaviourSupportStrategies.v1'
const GOAL_PROGRESS_KEY = 'schub.demo.behaviourGoalProgress.v1'

export const sessionNoteRecipients: { id: SessionNoteRecipient; label: string; detail: string }[] = [
  { id: 'participant', label: 'Participant', detail: 'Participant facing therapy update' },
  { id: 'parent_nominee', label: 'Parent or nominee', detail: 'Authorised family member or representative' },
  { id: 'support_workers', label: 'Support workers', detail: 'Practical information for day to day support' },
  { id: 'support_coordinator', label: 'Support coordinator', detail: 'Care coordination update' },
  { id: 'allied_health', label: 'Other allied health', detail: 'Shared where participant permission allows' },
]

export const sampleBehaviourSessionNote: BehaviourSessionNote = {
  id: 'beh-session-demo-1',
  participantId: 'leo',
  participantName: 'Leo',
  clinicianId: 'u-jordan',
  clinicianName: 'Jordan Blake',
  clinicianRole: 'Behaviour Support Practitioner',
  sessionDate: '2026-08-19',
  sessionType: 'Home visit',
  topic: 'Emotional regulation and after school transitions',
  clinicalNote: `Session focus: emotional regulation, transitions after school and communicating the need for a break.\n\nThe participant engaged with the red, amber and green regulation language. They identified amber during two supported examples and requested a break with one verbal prompt. Visual first then language and a five minute transition warning reduced the amount of repeated verbal prompting required.\n\nClinical observations: the participant responded more consistently when language was brief and paired with a visual cue. Increased frustration was observed when an expected activity changed without warning.\n\nPlan: continue practising recognition of early regulation cues, requesting a break and predictable transition supports. Review independence with break requests at the next session.`,
  careTeamSummary: `What we worked on today\nWe practised recognising when frustration was building and using the red, amber and green regulation system.\n\nWhat worked\nVisual prompts worked better than repeated verbal instructions.\nA five minute warning helped with transitions.\nThe participant was able to ask for a break with one verbal prompt.\n\nWhat the team can do\nKeep using the same red, amber and green language.\nWhen the participant identifies amber, reduce questions and offer their usual break choices.\nGive a five minute warning before changing activities where possible.`,
  strategies: [
    { id: 'strategy-transition', participantId: 'leo', title: 'Transition warning', when: 'Before changing activities', doThis: 'Give a clear five minute visual or verbal warning.', avoid: 'Avoid sudden changes where possible.', addToSupportStrategies: true },
    { id: 'strategy-amber', participantId: 'leo', title: 'Responding to amber', when: 'When amber is identified', doThis: 'Reduce verbal questions and offer known break choices.', avoid: 'Avoid repeated prompting during escalation.', addToSupportStrategies: true },
  ],
  goalLinks: [
    { id: 'goal-break', participantId: 'leo', goalTitle: 'Communicate when I need a break', progressNote: 'The participant identified amber and requested a break with one verbal prompt during the session.', addToGoalProgress: true },
  ],
  recipients: ['participant', 'parent_nominee', 'support_workers', 'support_coordinator'],
  status: 'draft',
  createdAt: '2026-08-19T06:40:00+10:00',
  updatedAt: '2026-08-19T06:40:00+10:00',
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = appStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

export function loadBehaviourSessionNotes(participantId?: string): BehaviourSessionNote[] {
  const notes = readJson<BehaviourSessionNote[]>(NOTES_KEY, [sampleBehaviourSessionNote])
  return participantId ? notes.filter(item => item.participantId === participantId) : notes
}

export function loadPublishedTherapyUpdates(participantId?: string): BehaviourSessionNote[] {
  return loadBehaviourSessionNotes(participantId).filter(note => note.status === 'shared')
}

export function loadBehaviourSupportStrategies(participantId?: string): BehaviourSessionStrategy[] {
  const stored = readJson<BehaviourSessionStrategy[]>(STRATEGIES_KEY, [])
  const items = stored.length ? stored : sampleBehaviourSessionNote.strategies
  return participantId ? items.filter(item => item.participantId === participantId) : items
}

export function loadBehaviourGoalProgress(participantId?: string): BehaviourSessionGoalLink[] {
  const items = readJson<BehaviourSessionGoalLink[]>(GOAL_PROGRESS_KEY, [])
  return participantId ? items.filter(item => item.participantId === participantId) : items
}

export function saveBehaviourSessionNote(note: BehaviourSessionNote): BehaviourSessionNote[] {
  const notes = loadBehaviourSessionNotes()
  const next = [note, ...notes.filter(item => item.id !== note.id)]
  appStorage.setItem(NOTES_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent('schub:behaviour-session-notes-change'))
  return next
}

export function publishBehaviourSessionNote(note: BehaviourSessionNote): BehaviourSessionNote[] {
  const now = new Date().toISOString()
  const shared: BehaviourSessionNote = { ...note, status: 'shared', updatedAt: now }
  const selectedStrategies = shared.strategies.filter(item => item.addToSupportStrategies).map(item => ({ ...item, participantId: shared.participantId }))
  const selectedGoals = shared.goalLinks.filter(item => item.addToGoalProgress).map(item => ({ ...item, participantId: shared.participantId }))

  if (selectedStrategies.length) {
    const existing = readJson<BehaviourSessionStrategy[]>(STRATEGIES_KEY, [])
    const next = [...selectedStrategies, ...existing.filter(old => !selectedStrategies.some(item => item.id === old.id && item.participantId === old.participantId))]
    appStorage.setItem(STRATEGIES_KEY, JSON.stringify(next))
  }

  if (selectedGoals.length) {
    const existing = readJson<BehaviourSessionGoalLink[]>(GOAL_PROGRESS_KEY, [])
    const next = [...selectedGoals, ...existing.filter(old => !selectedGoals.some(item => item.id === old.id && item.participantId === old.participantId))]
    appStorage.setItem(GOAL_PROGRESS_KEY, JSON.stringify(next))
  }

  window.dispatchEvent(new CustomEvent('schub:behaviour-strategies-change'))
  window.dispatchEvent(new CustomEvent('schub:behaviour-goal-progress-change'))
  window.dispatchEvent(new CustomEvent('schub:therapy-updates-change'))
  return saveBehaviourSessionNote(shared)
}

